"use client";

import Reveal from "@/components/Reveal";
import PhotoCarousel from "@/components/PhotoCarousel";
import { useTranslations } from "@/i18n/LocaleProvider";
import { DEVINA_GALLERY_IMAGES } from "@/lib/galleryImages";

const INSTAGRAM_URL = "https://www.instagram.com/hoop_yogi/";

// Not a live-fetched embed -- there's no Instagram API token configured for this project, and a
// fake/mocked feed would misrepresent what's actually posted. Links straight to the real,
// verified account (35.4k followers, see docs/research) so "recent posts" always means the real
// ones, just one tap away, instead of a stale or fabricated copy living on this site.
// `showCarousel` lets a page opt out if it already has its own photo carousel elsewhere -- every
// current caller wants it, but the option stays cheap to keep rather than assuming that holds.
// `compact` passes through to PhotoCarousel -- only the homepage (full-bleed 1440px column)
// needs the smaller cards to keep this section within one viewport; every other caller sits in
// a narrower content column already and keeps the default size.
export default function InstagramTeaser({
  showCarousel = true,
  compact = false,
}: {
  showCarousel?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslations();
  return (
    <Reveal>
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-accent">
        {t("home.instagram.eyebrow")}
      </p>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <h2 className="font-display text-3xl sm:text-4xl">{t("home.instagram.title")}</h2>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl border border-foreground/20 px-5 py-2.5 text-sm font-semibold transition hover:border-accent hover:text-accent"
        >
          <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden>
            <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.97.24 2.43.4a4.9 4.9 0 0 1 1.77 1.15 4.9 4.9 0 0 1 1.15 1.77c.16.46.35 1.26.4 2.43.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.24 1.97-.4 2.43a4.9 4.9 0 0 1-1.15 1.77 4.9 4.9 0 0 1-1.77 1.15c-.46.16-1.26.35-2.43.4-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.97-.24-2.43-.4a4.9 4.9 0 0 1-1.77-1.15 4.9 4.9 0 0 1-1.15-1.77c-.16-.46-.35-1.26-.4-2.43C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.24-1.97.4-2.43a4.9 4.9 0 0 1 1.15-1.77A4.9 4.9 0 0 1 5.59 1.8c.46-.16 1.26-.35 2.43-.4C9.29 1.34 9.67 1.33 12.87 1.33ZM12 6.87A5.13 5.13 0 1 0 17.13 12 5.13 5.13 0 0 0 12 6.87Zm0 8.46A3.33 3.33 0 1 1 15.33 12 3.33 3.33 0 0 1 12 15.33Zm5.34-8.66a1.2 1.2 0 1 0 1.2 1.2 1.2 1.2 0 0 0-1.2-1.2Z" />
          </svg>
          {t("home.instagram.cta")}
        </a>
      </div>
      {showCarousel && (
        <div className="mt-8">
          <PhotoCarousel images={DEVINA_GALLERY_IMAGES} compact={compact} />
        </div>
      )}
    </Reveal>
  );
}
